import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Checkbox } from "./ui/checkbox";
import { ResponseBadge } from "./ResponseBadge";
import { ConfirmDelete } from "./ConfirmDelete";
import { DoorRsvpRow } from "./DoorRsvpRow";
import { api, formatApiError } from "../lib/api";
import { formatStamp } from "../lib/format";

const HEADERS = ["Name", "Email", "Phone", "Response", "Extras", "Private note to host", "Public comment", "Updated", ""];
const dash = <span className="text-slate-300">—</span>;

export const RsvpTable = ({ eventId, rsvps, doorGuests = [], onChanged, onDoorChanged }) => {
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => setSelected((s) => s.filter((id) => rsvps.some((r) => r.id === id))), [rsvps]);

  const run = async (request, message) => {
    setBusy(true);
    try {
      await request();
      toast.success(message);
      setSelected([]);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const removeOne = (r) => run(() => api.delete(`/events/${eventId}/rsvps/${r.id}`), `Removed ${r.name}'s RSVP`);
  const removeDoor = (g) =>
    run(() => api.delete(`/events/${eventId}/door-guests/${g.id}`), `Removed ${g.name} from the guest list`).then(onDoorChanged);
  const removeSelected = () =>
    run(() => api.post(`/events/${eventId}/rsvps/bulk-delete`, { ids: selected }), `Removed ${selected.length} RSVP${selected.length > 1 ? "s" : ""}`);
  const toggle = (id, on) => setSelected((s) => (on ? [...s, id] : s.filter((x) => x !== id)));
  const allOn = rsvps.length > 0 && selected.length === rsvps.length;

  if (!rsvps.length && !doorGuests.length)
    return (
      <div data-testid="admin-rsvp-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white/60 p-12 text-center text-slate-500">
        No responses here yet. Share the link and they'll roll in.
      </div>
    );
  return (
    <>
      {selected.length > 0 && (
        <div data-testid="admin-bulk-bar" className="mb-3 flex items-center justify-between rounded-xl bg-slate-900 px-4 py-3 text-sm text-white">
          <span data-testid="admin-bulk-count">{selected.length} selected</span>
          <ConfirmDelete testid="admin-bulk-delete" busy={busy} onConfirm={removeSelected} title={`Remove ${selected.length} RSVP${selected.length > 1 ? "s" : ""}?`}>
            <button data-testid="admin-bulk-delete-btn" className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 font-semibold hover:bg-red-700">
              <Trash2 className="h-4 w-4" /> Delete selected
            </button>
          </ConfirmDelete>
        </div>
      )}
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <Table data-testid="admin-rsvp-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-10 pl-5">
                <Checkbox data-testid="admin-select-all" checked={allOn} onCheckedChange={(on) => setSelected(on ? rsvps.map((r) => r.id) : [])} aria-label="Select all" />
              </TableHead>
              {HEADERS.map((h) => (
                <TableHead key={h} className="whitespace-nowrap px-5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rsvps.map((r) => (
              <TableRow key={r.id} data-testid={`admin-rsvp-row-${r.id}`} data-state={selected.includes(r.id) ? "selected" : undefined}>
                <TableCell className="pl-5">
                  <Checkbox data-testid={`admin-select-${r.id}`} checked={selected.includes(r.id)} onCheckedChange={(on) => toggle(r.id, on)} aria-label={`Select ${r.name}`} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-5 py-4 font-semibold text-slate-900">
                  {r.name}
                  {r.is_additional && (
                    <span data-testid={`admin-rsvp-additional-total-${r.id}`} className="mt-1 block text-xs font-semibold uppercase tracking-[0.12em] text-blue-700">
                      Total additional guests
                    </span>
                  )}
                </TableCell>
                <TableCell data-testid="admin-rsvp-email" className="px-5 text-slate-600">{r.email || dash}</TableCell>
                <TableCell data-testid="admin-rsvp-phone" className="whitespace-nowrap px-5 text-slate-600">{r.phone || dash}</TableCell>
                <TableCell className="px-5"><ResponseBadge value={r.response} /></TableCell>
                <TableCell className="px-5 font-mono">{r.extra_guests}</TableCell>
                <TableCell data-testid="admin-rsvp-note" className="min-w-[10rem] max-w-xs break-words px-5 text-slate-600">{r.note || dash}</TableCell>
                <TableCell data-testid="admin-rsvp-comment" className="min-w-[10rem] max-w-xs break-words px-5 text-slate-600">{r.public_comment || dash}</TableCell>
                <TableCell className="whitespace-nowrap px-5 text-slate-500">{formatStamp(r.updated_at)}</TableCell>
                <TableCell className="pr-5 text-right">
                  <ConfirmDelete testid={`admin-delete-rsvp-${r.id}`} busy={busy} onConfirm={() => removeOne(r)} title={`Remove ${r.name}'s RSVP?`}>
                    <button data-testid={`admin-delete-rsvp-btn-${r.id}`} title="Delete RSVP" className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-[background-color,color] duration-200 hover:bg-red-50 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </ConfirmDelete>
                </TableCell>
              </TableRow>
            ))}
            {doorGuests.map((g) => <DoorRsvpRow key={g.id} guest={g} busy={busy} onDelete={removeDoor} />)}
          </TableBody>
        </Table>
      </div>
    </>
  );
};
